import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MeContext } from '../../App';
import Button from './Button';
import Checkbox from './Checkbox';
import ApiErrorMessages from './ApiErrorMessages';
import Loader from './Loader';
import { searchPrograms } from '../../services/programsService';
import { getForms, getClientForm, searchFormsInstances } from '../../services/formsService';
import { searchClients } from '../../services/clientsService';
import { CLIENT_ID } from '../../constants/formTypes';
import './StarterChecklist.css';

interface StepState {
    label: string;
    done: boolean;
    href?: string;
    disabled?: boolean;
}

const StarterChecklist: React.FC = () => {
    const me = useContext(MeContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<any>();

    const [hasProgram, setHasProgram] = useState(false);
    const [hasAssignedForm, setHasAssignedForm] = useState(false);
    const [hasClientForm, setHasClientForm] = useState(false);
    const [hasParticipant, setHasParticipant] = useState(false);
    const [hasSubmission, setHasSubmission] = useState(false);

    const [dismissed, setDismissed] = useState<boolean>(() => localStorage.getItem('starterChecklist:dismissed') === 'true');
    const [collapsed, setCollapsed] = useState<boolean>(true);

    const lastClientId = useMemo(() => localStorage.getItem('starterChecklist:lastClientId') || '', []);

    const canView = useMemo(() => {
        if (me.loading || !me.user) {
            return false;
        }
        return me.user.userRoleName === 'Admin' || me.user.userRoleName === 'Contributor';
    }, [me]);

    const location = useLocation();

    useEffect(() => {
        // Auto-minimize on route changes
        setCollapsed(true);
    }, [location.pathname]);

    useEffect(() => {
        if (!canView || dismissed) {
            return;
        }

        setLoading(true);
        setError(undefined);

        Promise.all([
            searchPrograms({ take: 1, includeDeactivated: false }),
            getForms(false, undefined, true),
            getClientForm(),
            searchClients({ query: '', skip: 0, take: 1, includeDeactivated: false })
        ]).then(async ([programsPage, forms, clientForm, clientsPage]) => {
            const hasAnyPrograms = (programsPage.totalCount || 0) > 0;
            setHasProgram(hasAnyPrograms);

            const assignedNonClientForms = forms.filter(f => f.formTypeId !== CLIENT_ID && !f.deactivated && !!f.programId);
            setHasAssignedForm(assignedNonClientForms.length > 0);

            setHasClientForm(!!clientForm && Array.isArray(clientForm.fields) && clientForm.fields.length > 0);

            const hasAnyParticipants = (clientsPage.totalCount || 0) > 0;
            setHasParticipant(hasAnyParticipants);

            // Form submissions anywhere in org (any type)
            try {
                const instances = await searchFormsInstances({ take: 1 });
                setHasSubmission((instances.totalCount || 0) > 0);
            } catch (e) {
                // Non-fatal
                console.error(e);
            }
        }).catch(e => {
            console.error(e);
            setError(e);
        }).finally(() => {
            setLoading(false);
        });
    }, [canView, dismissed]);

    const allDone = hasProgram && hasAssignedForm && hasClientForm && hasParticipant && hasSubmission;
    const completedCount = (hasProgram ? 1 : 0) + (hasAssignedForm ? 1 : 0) + (hasClientForm ? 1 : 0) + (hasParticipant ? 1 : 0) + (hasSubmission ? 1 : 0);

    const steps: StepState[] = [
        {
            label: 'Create a Program',
            done: hasProgram,
            href: '/admin/programs'
        },
        {
            label: 'Create a Form and assign it to a Program',
            done: hasAssignedForm,
            href: '/admin/forms'
        },
        {
            label: 'Create the Participant Additional Information form',
            done: hasClientForm,
            href: '/admin/forms'
        },
        {
            label: 'Add a Participant',
            done: hasParticipant,
            href: '/participants/add'
        },
        {
            label: 'Enroll a Participant and submit their first form',
            done: hasSubmission,
            href: lastClientId ? `/participants/${lastClientId}/programs` : '/tracking/delivery',
            disabled: !hasParticipant
        }
    ];

    const handleDismiss = () => {
        localStorage.setItem('starterChecklist:dismissed', 'true');
        setDismissed(true);
    };

    if (!canView || dismissed) { //|| allDone) {
        return null;
    }

    return (
        <div className={`starter-checklist hide-on-print ${allDone ? 'complete' : ''} ${collapsed ? 'collapsed' : ''}`} role="region" aria-label="Getting started checklist">
            {loading && <Loader noMarginTop={true} />}
            {!loading && <>
                {!collapsed && <>
                    <div className="row justify-content-between align-items-center">
                        <div className="column">
                            <div className="starter-checklist-title">Getting started</div>
                            {!allDone && <div className="starter-checklist-subtitle" aria-live="polite">{completedCount} of {steps.length} complete</div>}
                            {allDone && <div className="starter-checklist-subtitle complete" aria-live="polite"><i className="ri-check-double-line" aria-hidden="true" /> All done! You're set up. 🎉</div>}
                        </div>
                        <div className="column-auto">
                            <div className="row align-items-center">
                                <div className="column-auto">
                                    <Button id="minimize-starter-checklist" className="secondary small" text="Minimize" onClick={() => setCollapsed(true)} />
                                </div>
                                <div className="column-auto">
                                    <Button id="dismiss-starter-checklist" className="flat small" text="Dismiss" onClick={handleDismiss} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <ApiErrorMessages error={error} />
                    <div className="starter-checklist-steps">
                        {steps.map((s, i) => (
                            <div className="starter-checklist-step" key={i}>
                                <div className="row align-items-center justify-content-between">
                                    <div className="column">
                                        <Checkbox id={`step-${i}`} checked={s.done} onChange={() => {}} disabled={true} label={s.label} />
                                    </div>
                                    <div className="column-auto">
                                        <Button id={`step-cta-${i}`} text={s.done ? 'View' : 'Do this'} className="secondary small" href={s.href} disabled={s.disabled} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>}

                {collapsed && <div className="row justify-content-between align-items-center">
                    <div className="column">
                        <div className="starter-checklist-title">Getting started</div>
                        <div className="starter-checklist-subtitle" aria-live="polite">{completedCount} of {steps.length} complete</div>
                    </div>
                    <div className="column-auto">
                        <Button id="expand-starter-checklist" className="secondary small" text="Open" onClick={() => setCollapsed(false)} />
                    </div>
                </div>}
            </>}
        </div>
    );
};

export default StarterChecklist;


